
interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number; // time to live in ms
}

class DataCache {
  private cache = new Map<string, CacheItem<any>>();
  private readonly DEFAULT_TTL = 1000 * 60 * 15; // 15 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    try {
      const item: CacheItem<T> = {
        data,
        timestamp: Date.now(),
        ttl
      };
      
      this.cache.set(key, item);
      
      // Also store in localStorage for persistence
      localStorage.setItem(`cache_${key}`, JSON.stringify(item));
      console.log(`[DataCache] Cached data for key: ${key}`);
    } catch (error) {
      console.warn(`[DataCache] Failed to cache data for key: ${key}`, error);
    }
  }

  get<T>(key: string): T | null {
    try {
      // First try memory cache
      let item = this.cache.get(key);
      
      // Fallback to localStorage
      if (!item) {
        const stored = localStorage.getItem(`cache_${key}`);
        if (stored) {
          const parsedItem = JSON.parse(stored);
          if (parsedItem && typeof parsedItem === 'object' && 'data' in parsedItem && 'timestamp' in parsedItem && 'ttl' in parsedItem) {
            item = parsedItem as CacheItem<any>;
            this.cache.set(key, item); // Restore to memory
          }
        }
      }
      
      if (!item) return null;
      
      // Check if expired
      const isExpired = Date.now() - item.timestamp > item.ttl;
      if (isExpired) {
        this.remove(key);
        return null;
      }
      
      console.log(`[DataCache] Retrieved cached data for key: ${key}`);
      return item.data;
    } catch (error) {
      console.warn(`[DataCache] Failed to retrieve cached data for key: ${key}`, error);
      return null;
    }
  }

  remove(key: string): void {
    this.cache.delete(key);
    localStorage.removeItem(`cache_${key}`);
  }

  clear(): void {
    this.cache.clear();
    // Clear all cache items from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key);
      }
    });
  }
}

export const dataCache = new DataCache();
